import React, { useMemo } from 'react'
import isEmpty from 'lodash/isEmpty'
import type { Task } from '@ztechapps/common.contract'
import { TaskStatusEnum } from '@ztechapps/common.contract'
import { DataSuspense } from '@ztechapps/webui.util'
import type { TaskBoardGroupByEnum } from '@ztechapps/task.common'
import { groupTasksByStatus } from '@ztechapps/task.common'
import { TaskBoardProvider, useTaskBoardContext } from './Context'
import { useTaskFilterBarContext } from '../TaskFilterBar/Context'
import { TaskListEmptyContent } from '../shared/TaskListEmptyContent'
import { TaskBoardColumn } from './TaskBoardColumn'
import styles from './TaskBoard.module.scss'

type TaskBoardProps = {
  tasks: Task[]
  loading: boolean
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
  visibleStatuses?: TaskStatusEnum[]
  refetchQueries?: string[]
  groupBy?: TaskBoardGroupByEnum
  height?: string
  canEdit?: boolean
}

const STATUSES = [TaskStatusEnum.Draft, TaskStatusEnum.InProgress, TaskStatusEnum.Complete, TaskStatusEnum.Cancelled]

type TaskBoardContentProps = {
  tasks: Task[]
  loading: boolean
  onDoubleClick?: (taskId: string, event: React.MouseEvent) => void
  visibleStatuses?: TaskStatusEnum[]
  groupBy?: TaskBoardGroupByEnum
  height?: string
}

const TaskBoardContent = ({
  tasks,
  loading,
  onDoubleClick,
  visibleStatuses: visibleStatusesProp,
  groupBy,
  height,
}: TaskBoardContentProps) => {
  const { optimisticTransitions } = useTaskBoardContext()
  const { filterState } = useTaskFilterBarContext()
  const visibleStatuses = useMemo(
    () =>
      visibleStatusesProp ??
      (filterState?.statuses ? STATUSES.filter((s) => filterState.statuses.includes(s)) : STATUSES),
    [visibleStatusesProp, filterState?.statuses]
  )

  const optimisticTasks = useMemo(() => {
    if (isEmpty(optimisticTransitions)) return tasks
    return tasks.map((task) => {
      const transition = optimisticTransitions.find((t) => t.taskId === task._id)
      if (transition) {
        return { ...task, status: transition.toStatus }
      }
      return task
    })
  }, [tasks, optimisticTransitions])

  const tasksByStatus = useMemo(() => groupTasksByStatus(optimisticTasks), [optimisticTasks])

  return (
    // Raw div: vstack adds flex:1 1 auto which conflicts with the explicit height in boardWrapper SCSS
    <div
      className={`${styles.boardWrapper} d-flex flex-column overflow-hidden`}
      style={height ? { height } : undefined}
    >
      <DataSuspense
        loading={loading && isEmpty(tasks)}
        isEmpty={isEmpty(tasks)}
        emptyComponent={<TaskListEmptyContent />}
      >
        {/* Raw div: columns need align-items:stretch (default), HorizontalStack forces align-items:center */}
        <div className={`${styles.board} d-flex gap-3 overflow-auto pb-2 flex-fill`}>
          {visibleStatuses.map((status) => (
            <TaskBoardColumn
              key={status}
              status={status}
              tasks={tasksByStatus[status]}
              onDoubleClick={onDoubleClick}
              groupBy={groupBy}
            />
          ))}
        </div>
      </DataSuspense>
    </div>
  )
}

export const TaskBoard = ({
  tasks,
  loading,
  onDoubleClick,
  visibleStatuses,
  refetchQueries,
  groupBy,
  height,
  canEdit = true,
}: TaskBoardProps) => {
  return (
    <TaskBoardProvider refetchQueries={refetchQueries} canEdit={canEdit}>
      <TaskBoardContent
        tasks={tasks}
        loading={loading}
        onDoubleClick={onDoubleClick}
        visibleStatuses={visibleStatuses}
        groupBy={groupBy}
        height={height}
      />
    </TaskBoardProvider>
  )
}
